/* eslint-disable @typescript-eslint/prefer-optional-chain */
import { StatusCodes } from 'http-status-codes';
import React, { useEffect, useState } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import { FnbOrderGetWithItemsDto, QuantityGetDto, toLocalEquivalentUtcDate, } from 'svila-common';
import { v4 as uuid } from 'uuid';
import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import InventoryApi from '../../../../services/api/InventoryApi';
import PuffLoader from 'react-spinners/PuffLoader';
import TableWrapper from '~/src/components/common/forms/TableWrapper/TableWrapper';
import GtinComponent from '~/src/components/common/GtinComponent';
import { DATE_FORMAT } from '~/src/utils/constants';
import moment from 'moment';
import FnbApi from '~/src/services/api/FnbApi';
import TitleComponent from '~/src/components/common/forms/Title/TitleComponent';

interface ParamProps {
  id: string;
  consolidation: boolean;
}

interface DataRowTypes {
  index: number;
  lot: string;
  barcode: string;
  name: string;
  fullDuUnits: number;
  singleDpUnits: number;
  singleCuUnits: number;
  totalCuUnits: number;
  handleClick: any;
  isChecked: any;
  id: string;
  isInvoiced: boolean;
  isRemoved: boolean;
  genericId: string;
}

export default function PrintLotReport(): React.ReactElement {
  const { id, consolidation } = useParams<ParamProps>();
  const history = useHistory();
  const [copyResult, setCopyResult] = useState('');
  const [union, setUnion] = useState<QuantityGetDto[]>([]);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [ordersX, setOrders] = useState<FnbOrderGetWithItemsDto[]>([]);
  // loading for orders
  const [loading, setLoading] = useState<boolean>(false);
  const [isCheck, setIsCheck] = useState([]);

  const getQuantities = async (): Promise<void> => {
    setLoadingData(true);
    const res = await InventoryApi.getQuantitiesForSellOrder(id);
    if (res.status === StatusCodes.OK) {
      setUnion(res.data);
    }
    setLoadingData(false);
  };

  const getOrders = async (): Promise<void> => {
    setLoading(true)
    const res = await FnbApi.getFnbOrderForConsolidation(id);
    if (res.status === StatusCodes.OK) {
      setOrders(res.data);
    }
    setLoading(false)
  };

  useEffect(() => {
    if (id) {
      getQuantities();
      getOrders();
    }
  }, []);

  const handleClick = (e) => {
    const { id, checked } = e.target;
    setIsCheck([...isCheck, id]);
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id));
    }
  };

  const DataRow = ({
    barcode,
    lot,
    name,
    fullDuUnits,
    singleDpUnits,
    singleCuUnits,
    totalCuUnits,
    index,
    handleClick,
    isChecked,
    id,
    isInvoiced,
    isRemoved,
    genericId,
    expirationDate,
  }: DataRowTypes): React.ReactElement => {
    return (
      <tr>

        <td style={{ padding: 10, border: '1px solid gray' }}>
          <GtinComponent text="CU" code={barcode} background="#F4F4F4" />
        </td>
        <td style={{ padding: 10, border: '1px solid gray' }}>
          {lot}
        </td>
        <td style={{ padding: 10, border: '1px solid gray' }}>
          {expirationDate}
        </td>
        <td style={{ padding: 10, border: '1px solid gray', textAlign: 'left', color: '#343434' }}>
          <Link style={{ color: '#343434' }} to={`/inventory/list-for-generic/${genericId}`} target="_blank">
            {name}
          </Link>
        </td>
        <td style={{ padding: 10, border: '1px solid gray', fontWeight: 'bold' }}>{fullDuUnits}</td>
        <td style={{ padding: 10, border: '1px solid gray' }}>{singleDpUnits}</td>
        <td style={{ padding: 10, border: '1px solid gray' }}>{singleCuUnits}</td>
        <td style={{ padding: 10, border: '1px solid gray' }}>{totalCuUnits}</td>
      </tr>
    );
  };

  const rowSum = (item: QuantityGetDto[]): React.ReactElement => {
    let fullDuUnitsSum = 0;
    let singleDpUnitsSum = 0;
    let singleCuUnitsSum = 0;
    let totalCuUnitsSum = 0;
    for (let i = 0; i < item.length; i++) {
      fullDuUnitsSum = fullDuUnitsSum + (item[i].fullDuUnits ?? 0);
      singleDpUnitsSum = singleDpUnitsSum + (item[i].singleDpUnits ?? 0);
      singleCuUnitsSum = singleCuUnitsSum + (item[i].singleCuUnits ?? 0);
      totalCuUnitsSum = totalCuUnitsSum + item[i].totalCuUnits;
    }

    return (
      <tr>
        <td style={{ padding: 10, border: '1px solid gray' }} colSpan={4}>TOTAL</td>
        <td style={{ padding: 10, border: '1px solid gray', fontWeight: 'bold' }}>{fullDuUnitsSum}</td>
        <td style={{ padding: 10, border: '1px solid gray' }}>
          <span>{singleDpUnitsSum}</span>
        </td>
        <td style={{ padding: 10, border: '1px solid gray' }}>{singleCuUnitsSum}</td>
        <td style={{ padding: 10, border: '1px solid gray' }}>{totalCuUnitsSum}</td>
      </tr>
    );
  };

  interface OrderRow {
    order: FnbOrderGetWithItemsDto;
  }
  
  const OrdersDataRow = ({ order }: OrderRow): React.ReactElement => {
    const date = moment(toLocalEquivalentUtcDate(order.orderDate)).format(DATE_FORMAT);
    return (
      <tr>

        <td style={{ padding: 10, border: '1px solid gray' }}>
          {date}
        </td>
        <td style={{ padding: 10, border: '1px solid gray' }}>
          {order.uniqueNumber}
        </td>
        <td style={{ padding: 10, border: '1px solid gray' }}>
          {order.order.orderNumber}
        </td>
        <td style={{ padding: 10, border: '1px solid gray' }}>
          {order.name}
        </td>
        <td style={{ padding: 10, border: '1px solid gray' }}>
          {order.nameLast}
        </td>
        <td style={{ padding: 10, border: '1px solid gray' }}>
          {order.paymentMethodTxt}
        </td>
        <td style={{ padding: 10, border: '1px solid gray' }}>
          {order.cityName}
        </td>
        <td style={{ padding: 10, border: '1px solid gray' }}>
          {order.total}
        </td>

      </tr>
    );
  };

  return (
    <WhitePad>
      { <TitleComponent
        isBig
        onLeftHandler={history.goBack}
        title="Full Report"
      /> }
      {union &&
        (loadingData ? (
          <PuffLoader color="#000000" loading={true} size={25} />
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <p></p>
              <p>{copyResult}</p>
            </div>
            <br />
            <br />
            <br />
            <TableWrapper>
              <thead>
                <tr>
                  <th style={{ padding: 10, border: '1px solid gray' }}>MNum</th>
                  <th style={{ padding: 10, border: '1px solid gray' }}>Exp Date</th>
                  <th style={{ padding: 10, border: '1px solid gray' }}>lot</th>
                  <th style={{ padding: 10, border: '1px solid gray' }}>MName</th>
                  <th style={{ padding: 10, border: '1px solid gray' }}>Du</th>
                  <th style={{ padding: 10, border: '1px solid gray' }}>Dp</th>
                  <th style={{ padding: 10, border: '1px solid gray' }}>Cu</th>
                  <th style={{ padding: 10, border: '1px solid gray' }}>Total Cu</th>
                </tr>
              </thead>
              <tbody>
                {union &&
                  union.map((item, index) => {
                    const idx: string = uuid() as string;
                    return (
                      <DataRow
                        handleClick={handleClick}
                        lot={item.lot}
                        isChecked={isCheck.includes(item.tempId)}
                        id={item.tempId}
                        index={index}
                        key={idx}
                        barcode={item.cuGtin}
                        name={item.product.genericProduct.name}
                        fullDuUnits={item.fullDuUnits ?? 0}
                        singleDpUnits={item.singleDpUnits ?? 0}
                        singleCuUnits={item.singleCuUnits ?? 0}
                        totalCuUnits={item.totalCuUnits}
                        isInvoiced={item.isInvoiced}
                        isRemoved={item.isRemoved}
                        genericId={item.product.genericProductId}
                        expirationDate={moment(toLocalEquivalentUtcDate(item.expirationDate)).format(DATE_FORMAT)}
                      />
                    );
                  })}
                {union && rowSum(union)}
              </tbody>
            </TableWrapper>
          </>
        ))}
      <br />
      <br />
      <br />
      {
        loadingData ? (
          <PuffLoader color="#000000" loading={true} size={25} />
        ) : (
          <TableWrapper>
            <thead>
              <tr>
                {/* <th style={{ padding: 10, border: '1px solid gray' }}>Status</th> */}
                <th style={{ padding: 10, border: '1px solid gray' }}>Order Date</th>
                <th style={{ padding: 10, border: '1px solid gray' }}>Fnb #</th>
                <th style={{ padding: 10, border: '1px solid gray' }}>Svila #</th>
                <th style={{ padding: 10, border: '1px solid gray' }}>Име</th>
                <th style={{ padding: 10, border: '1px solid gray' }}>Фамилия</th>
                <th style={{ padding: 10, border: '1px solid gray' }}>Метод на Плащане</th>
                <th style={{ padding: 10, border: '1px solid gray' }}>Град</th>
                <th style={{ padding: 10, border: '1px solid gray' }}>Сума</th>
                {/* <th style={{ padding: 10, border: '1px solid gray' }}>Actions</th>
              <th style={{ padding: 10, border: '1px solid gray' }}>Packed</th>
              <th style={{ padding: 10, border: '1px solid gray' }}>Reservation</th>
              <th style={{ padding: 10, border: '1px solid gray' }}>Problem</th> */}
              </tr>
            </thead>
            <tbody>
              {ordersX?.map((item, index) => {
                const idx: string = uuid() as string;
                return (
                  <OrdersDataRow key={idx} order={item} />
                );
              })}
            </tbody>
          </TableWrapper>
        )}
    </WhitePad>
  );
}